import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import styled from '@emotion/styled';

import UserProfile from './components/UserProfile';
import BicycleCard from './components/BicycleCard';
import { ActionButton } from './components/ActionButton';
import { URCodeFooter } from './components/URCodeFooter';

import { getOwnerId, checkOwnershipAPI, registerOwnerAPI } from './api/bicycle';
import { getCompleteOwnerInfo, storeOwnerInfoObject } from './utils/localStorage';

import { Product, OwnershipCheckResponse, OwnerInfo } from './types';
import { getProductByUrcode } from './mocks/products';

const BicyclePage: React.FC = () => {
  const { urcode } = useParams<{ urcode: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState<Product | null>(null);
  const [ownership, setOwnership] = useState<OwnershipCheckResponse | null>(null);
  const [ownerInfo, setOwnerInfo] = useState<OwnerInfo | null>(null);
  const [registering, setRegistering] = useState(false);

  // URL 파라미터에서 from 값 확인
  const searchParams = new URLSearchParams(location.search);
  const fromOwnerList = searchParams.get('from') === 'owner-list';

  useEffect(() => {
    const initializeData = async () => {
      if (!urcode) return;

      try {
        setLoading(true);

        // 1. 제품 정보 가져오기 (모킹)
        const productData = getProductByUrcode(urcode);
        if (!productData) {
          console.error('Product not found');
          setLoading(false);
          return;
        }
        setProduct(productData);

        // 2. Owner 정보 확인/발급
        let currentOwnerInfo = getCompleteOwnerInfo();
        
        if (!currentOwnerInfo) {
          // 완전한 정보가 없으면 API 호출하여 새로 발급
          try {
            const response = await getOwnerId();
            currentOwnerInfo = {
              ownerId: response.ownerId,
              name: response.name,
              phone: response.phone,
            };
            
            // 로컬스토리지에 저장
            storeOwnerInfoObject(currentOwnerInfo);
          } catch (error) {
            console.error('Failed to get owner ID:', error);
            setLoading(false);
            return;
          }
        }
        
        setOwnerInfo(currentOwnerInfo);

        // 3. 소유권 확인
        try {
          const ownershipData = await checkOwnershipAPI(urcode);
          setOwnership(ownershipData);

          // 4. 현재 사용자가 소유자인지 확인
          const isMyProduct = ownershipData.hasOwn && 
                             ownershipData.ownerId === currentOwnerInfo.ownerId;

          // 5. 내 제품이고, owner-list에서 온 것이 아니면 owner-list로 리다이렉션
          if (isMyProduct && !fromOwnerList) {
            navigate('/bicycle/owner-list', { replace: true });
            return;
          }
        } catch (error) {
          console.error('Failed to check ownership:', error);
          // 소유권 확인 실패 시 기본값 설정
          setOwnership({
            hasOwn: false,
            ownerId: '',
            ownerPhone: '',
          });
        }
      } catch (error) {
        console.error('Error initializing data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (urcode) {
      initializeData();
    }
  }, [urcode, navigate, fromOwnerList]);

  // 소유자 등록 핸들러
  const handleRegisterOwner = async () => {
    if (!urcode || !ownerInfo || registering) return;

    try {
      setRegistering(true);
      await registerOwnerAPI(urcode, ownerInfo.ownerId);

      // 등록 성공 후 소유 제품 목록 페이지로 이동
      navigate('/bicycle/owner-list');
    } catch (error) {
      console.error('Error registering owner:', error);
      alert('Failed to register ownership. Please try again.');
    } finally {
      setRegistering(false);
    }
  };

  // 소유자에게 연락하기 핸들러
  const handleContactOwner = () => {
    if (ownership?.ownerPhone) {
      // 전화번호로 연락하기 기능
      window.location.href = `tel:${ownership.ownerPhone}`;
    }
  };

  // 소유 제품 목록 보기
  const handleViewOwnerList = () => {
    navigate('/bicycle/owner-list');
  };

  // 뒤로가기
  const handleGoBack = () => {
    navigate('/bicycle/owner-list');
  };

  // 날짜 포맷팅 함수
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Container>
        <LoadingText>Loading...</LoadingText>
      </Container>
    );
  }

  if (!product || !ownership) {
    return (
      <Container>
        <ErrorText>Unable to load product information.</ErrorText>
      </Container>
    );
  }

  const hasOwner = ownership.hasOwn;
  // 현재 사용자가 소유자인지 확인
  const isMyProduct = hasOwner && ownership.ownerId === ownerInfo?.ownerId;
  const showAsMyProduct = isMyProduct && fromOwnerList;
  
  // 저장된 이름을 사용하되, 없으면 기본값 사용
  const displayName = ownerInfo?.name || 'Jisoo Park';

  return (
    <Container>
      <ContentWrapper>
        {showAsMyProduct && (
          <StatusBadge>✅ My Registered Bicycle</StatusBadge>
        )}
        
        <UserProfile userName={displayName} />
        
        <ProductInfo>
          <ProductName>{product.product.productName}</ProductName>
          <SerialNumber>Bicycle Serial Number {product.product.productNum}</SerialNumber>
          {hasOwner && (
            <RegistrationInfo>
              {isMyProduct ? (
                ownership.registerDate && `Registered on ${formatDate(ownership.registerDate)}`
              ) : (
                ownership.ownerName && `Registered Owner ${ownership.ownerName}`
              )}
            </RegistrationInfo>
          )}
        </ProductInfo>

        <BicycleCard product={product} />

        <ButtonWrapper>
          {showAsMyProduct ? (
            // owner-list에서 온 경우: 뒤로가기 버튼만 표시
            <ActionButton text="Back to My Bicycles" onClick={handleGoBack} />
          ) : (
            // 일반적인 경우
            <>
              {hasOwner ? (
                <ActionButton text="Contact bicycle owner" onClick={handleContactOwner} />
              ) : (
                <ActionButton text="Register bicycle ownership" onClick={handleRegisterOwner} disabled={registering} />
              )}

              <ActionButton text="Show Registered bicycles" onClick={handleViewOwnerList} />
            </>
          )}
        </ButtonWrapper>
      </ContentWrapper>

      <URCodeFooter />
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #2595FF 0%, #69C8C9 100%);
  display: flex;
  flex-direction: column;
  position: relative;
`;

const ContentWrapper = styled.div`
  flex: 1;
  padding: 40px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 120px;
`;

const StatusBadge = styled.div`
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid white;
  border-radius: 24px;
  padding: 8px 20px;
  color: white;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 20px;
  backdrop-filter: blur(10px);
`;

const ProductInfo = styled.div`
  text-align: center;
  margin: 20px 0;
`;

const ProductName = styled.h2`
  color: white;
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 8px 0;
`;

const SerialNumber = styled.p`
  color: white;
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 4px 0;
`;

const RegistrationInfo = styled.p`
  color: white;
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  opacity: 0.9;
`;

const ButtonWrapper = styled.div`
  width: 100%;
  max-width: 360px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 40px;
  align-items: center;
`;

const LoadingText = styled.p`
  color: white;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  margin-top: 50vh;
`;

const ErrorText = styled.p`
  color: white;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  margin-top: 50vh;
`;

export default BicyclePage;
